import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { renderMarkdown } from '@sd/shared/wiki';
import { api, type WikiPageDto, type WriteInput, type Revision } from '../lib/api.js';
import { useTheme, themeClass } from '../hooks/useTheme.js';
import '../styles/editor.css';

type Mode = 'split' | 'source' | 'preview';

interface DraftState {
  slug: string;
  titleUk: string;
  titleEn: string;
  category: string;
  type: string;
  treePath: string;
  related: string;          // редаговано як рядок з комами
  bodyMd: string;
  frontmatter: Record<string, unknown>;
}

const emptyDraft = (): DraftState => ({
  slug: '',
  titleUk: '',
  titleEn: '',
  category: '',
  type: '',
  treePath: '_root',
  related: '',
  bodyMd: '',
  frontmatter: {},
});

/**
 * Чернетка з префіл-полями (для /wiki/new?slug=...&title_uk=...&category=...).
 * Дозволяє «+ Створити сторінку» з вкладки скарбів пригоди відкрити редактор,
 * де slug+title+category вже заповнені.
 */
function draftFromQuery(params: URLSearchParams): DraftState {
  const get = (k: string): string => params.get(k) ?? '';
  return {
    slug: get('slug'),
    titleUk: get('title_uk') || get('titleUk'),
    titleEn: get('title_en') || get('titleEn'),
    category: get('category'),
    type: get('type'),
    treePath: get('treePath') || get('treepath') || '_root',
    related: get('related'),
    bodyMd: '',
    frontmatter: {},
  };
}

function toDraft(p: WikiPageDto): DraftState {
  return {
    slug: p.slug,
    titleUk: p.titleUk,
    titleEn: p.titleEn,
    category: p.category,
    type: p.type,
    treePath: p.treePath,
    related: p.related.join(', '),
    bodyMd: p.bodyMd,
    frontmatter: p.frontmatter,
  };
}

function toInput(d: DraftState): WriteInput {
  const fm = { ...d.frontmatter };
  fm.slug = d.slug;
  fm.title_uk = d.titleUk;
  fm.title_en = d.titleEn;
  fm.category = d.category;
  fm.type = d.type;
  const related = d.related.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);
  if (related.length) fm.related = related;
  else delete fm.related;
  return {
    slug: d.slug,
    titleUk: d.titleUk,
    titleEn: d.titleEn,
    category: d.category,
    type: d.type,
    treePath: d.treePath,
    related,
    bodyMd: d.bodyMd,
    frontmatter: fm,
  };
}

export function WikiEditor({ mode: action }: { mode: 'edit' | 'new' }): JSX.Element {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();

  const [draft, setDraft] = useState<DraftState | null>(
    action === 'new'
      ? (searchParams.toString() ? draftFromQuery(searchParams) : emptyDraft())
      : null,
  );
  const [mode, setMode] = useState<Mode>('split');
  const [knownSlugs, setKnownSlugs] = useState<Set<string>>(new Set());
  const [revisions, setRevisions] = useState<Revision[] | null>(null);
  const [showRevisions, setShowRevisions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [ac, setAc] = useState<{ x: number; y: number; query: string; active: number } | null>(null);

  // Завантаження сторінки для edit-режиму
  useEffect(() => {
    if (action !== 'edit') return;
    if (!slug) return;
    api.wikiPage(slug).then((p) => { setDraft(toDraft(p)); setDirty(false); }).catch((e: Error) => setError(e.message));
  }, [action, slug]);

  // Завантажуємо всі slug-и для wikilink-валідації і автодоповнення
  useEffect(() => {
    api.wikiSlugs().then((r) => setKnownSlugs(new Set(r.slugs))).catch(() => { /* noop */ });
  }, []);

  // Debounced bodyMd — preview перерендериться лише через 200 ms тиші.
  // Інакше unified+sanitize ланцюг на кожен keystroke джанкає введення.
  const [debouncedBody, setDebouncedBody] = useState<string>(draft?.bodyMd ?? '');
  useEffect(() => {
    if (!draft) return;
    const h = setTimeout(() => setDebouncedBody(draft.bodyMd), 200);
    return () => clearTimeout(h);
  }, [draft?.bodyMd]);

  // Live-preview HTML — рендериться з debounced версії.
  const previewHtml = useMemo(() => {
    try {
      return renderMarkdown(debouncedBody, { knownSlugs });
    } catch (e) {
      return `<div class="wiki-error">Помилка рендеру: ${String((e as Error).message)}</div>`;
    }
  }, [debouncedBody, knownSlugs]);

  // Попередження при закритті, якщо незбережені зміни
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [dirty]);

  const updateField = useCallback(<K extends keyof DraftState>(key: K, value: DraftState[K]) => {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    setDirty(true);
  }, []);

  const onSave = async (): Promise<void> => {
    if (!draft) return;
    if (!draft.slug.trim()) { setError('Slug обовʼязковий'); return; }
    setBusy(true);
    setError(null);
    try {
      const input = toInput(draft);
      if (action === 'new') {
        await api.wikiCreate(input);
      } else {
        await api.wikiUpdate(slug, input);
      }
      setDirty(false);
      navigate(`/wiki/${encodeURIComponent(input.slug)}`);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const onRename = async (): Promise<void> => {
    if (!draft || action !== 'edit') return;
    const next = window.prompt('Новий slug:', draft.slug);
    if (!next || next === draft.slug) return;
    setBusy(true);
    try {
      const res = await api.wikiRename(slug, next);
      const note = res.inboundUpdated > 0
        ? `Перейменовано. Оновлено wikilink-ів у ${res.inboundUpdated} сторінках.`
        : 'Перейменовано.';
      window.alert(note);
      navigate(`/wiki/${encodeURIComponent(next)}/edit`);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const onDelete = async (): Promise<void> => {
    if (action !== 'edit') return;
    if (!window.confirm(`Видалити сторінку ${slug}? Ревізії збережуться.`)) return;
    setBusy(true);
    try {
      await api.wikiDelete(slug);
      navigate('/wiki');
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const loadRevisions = async (): Promise<void> => {
    if (revisions !== null) { setShowRevisions((v) => !v); return; }
    try {
      const r = await api.wikiRevisions(slug);
      setRevisions(r.revisions);
      setShowRevisions(true);
    } catch (e) { setError((e as Error).message); }
  };

  const onRestore = async (id: number): Promise<void> => {
    if (!window.confirm('Відкотити до цієї ревізії? Поточна версія збережеться у ревізіях.')) return;
    setBusy(true);
    try {
      await api.wikiRestore(slug, id);
      const p = await api.wikiPage(slug);
      setDraft(toDraft(p));
      setDirty(false);
      setShowRevisions(false);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  // Автодоповнення wikilink при наборі `[[`
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const onBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    updateField('bodyMd', e.target.value);
    detectAutocomplete(e.target);
  };

  function detectAutocomplete(ta: HTMLTextAreaElement): void {
    const pos = ta.selectionStart;
    const upto = ta.value.slice(0, pos);
    const m = upto.match(/\[\[([^\]|\n]*)$/);
    if (!m) { setAc(null); return; }
    const query = m[1] ?? '';
    // Псевдо-розрахунок позиції на основі курсора в textarea
    // (грубо: розмістимо біля cursorPos через caretCoordinates emulation)
    const coords = approximateCaret(ta);
    setAc({ x: coords.x, y: coords.y, query, active: 0 });
  }

  const acMatches = useMemo(() => {
    if (!ac) return [];
    const q = ac.query.toLowerCase();
    const out: string[] = [];
    for (const s of knownSlugs) {
      if (s.toLowerCase().includes(q)) {
        out.push(s);
        if (out.length >= 12) break;
      }
    }
    return out;
  }, [ac, knownSlugs]);

  const insertWikilink = (slug: string): void => {
    if (!textareaRef.current || !ac) return;
    const ta = textareaRef.current;
    const pos = ta.selectionStart;
    const upto = ta.value.slice(0, pos);
    const m = upto.match(/\[\[([^\]|\n]*)$/);
    if (!m) { setAc(null); return; }
    const start = pos - m[0].length;
    const inserted = `[[${slug}]]`;
    const next = ta.value.slice(0, start) + inserted + ta.value.slice(pos);
    updateField('bodyMd', next);
    setAc(null);
    setTimeout(() => {
      ta.focus();
      const caret = start + inserted.length;
      ta.setSelectionRange(caret, caret);
    }, 0);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (ac && acMatches.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setAc({ ...ac, active: (ac.active + 1) % acMatches.length }); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setAc({ ...ac, active: (ac.active - 1 + acMatches.length) % acMatches.length }); return; }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const pick = acMatches[ac.active];
        if (pick) insertWikilink(pick);
        return;
      }
      if (e.key === 'Escape') { e.preventDefault(); setAc(null); return; }
    }
    if ((e.ctrlKey || e.metaKey) && e.key === 's') { e.preventDefault(); void onSave(); }
  };

  if (action === 'edit' && !draft) {
    return <div className="wiki-loading">завантаження…</div>;
  }

  if (!draft) return <div className="wiki-error">Чорт, чомусь немає чернетки</div>;

  return (
    <div className={`editor-shell ${themeClass(theme)}`.trim()}>
      <div className="editor-bar">
        <span className="badge">{action === 'new' ? 'Створення' : 'Редагування'}</span>
        <Link to={`/wiki/${encodeURIComponent(slug)}`} className="btn" style={{ textDecoration: 'none' }}>← Назад</Link>
        <div className="mode-toggle">
          <button className={mode === 'source' ? 'active' : ''} onClick={() => setMode('source')}>Джерело</button>
          <button className={mode === 'split' ? 'active' : ''} onClick={() => setMode('split')}>Сплит</button>
          <button className={mode === 'preview' ? 'active' : ''} onClick={() => setMode('preview')}>Рендер</button>
        </div>
        <div className="spacer" />
        {action === 'edit' && (
          <>
            <button className="btn" onClick={() => void loadRevisions()}>Історія</button>
            <button className="btn" onClick={() => void onRename()} disabled={busy}>Перейменувати</button>
            <button className="btn danger" onClick={() => void onDelete()} disabled={busy}>Видалити</button>
          </>
        )}
        <button className="btn primary" onClick={() => void onSave()} disabled={busy || !dirty}>
          {busy ? 'Зберігаю…' : dirty ? 'Зберегти' : 'Збережено'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '10px 24px', background: 'rgba(157,79,74,0.15)', color: 'var(--blood-bright)', fontSize: 13 }}>
          ⚠ {error}
        </div>
      )}

      <div className="fm-strip">
        <FmField label="slug" value={draft.slug} onChange={(v) => updateField('slug', v)} mono required />
        <FmField label="treePath" value={draft.treePath} onChange={(v) => updateField('treePath', v)} mono />
        <FmField label="title_uk" value={draft.titleUk} onChange={(v) => updateField('titleUk', v)} />
        <FmField label="title_en" value={draft.titleEn} onChange={(v) => updateField('titleEn', v)} />
        <FmField label="category" value={draft.category} onChange={(v) => updateField('category', v)} />
        <FmField label="type" value={draft.type} onChange={(v) => updateField('type', v)} />
        <FmField label="related (через кому)" value={draft.related} onChange={(v) => updateField('related', v)} mono />
      </div>

      <div className={`split ${mode === 'source' ? 'source-only' : mode === 'preview' ? 'preview-only' : ''}`}>
        {mode !== 'preview' && (
          <div className="pane">
            <div className="pane-header">джерело · markdown</div>
            <textarea
              ref={textareaRef}
              className="editor-textarea"
              value={draft.bodyMd}
              onChange={onBodyChange}
              onKeyDown={onKeyDown}
              onBlur={() => setTimeout(() => setAc(null), 200)}
              spellCheck={false}
              placeholder="# Заголовок&#10;&#10;Тіло. Використовуй [[slug]] для wikilink-ів, :::hero/:::cards для блоків."
            />
            {ac && (
              <Autocomplete
                x={ac.x}
                y={ac.y}
                matches={acMatches}
                active={ac.active}
                onPick={insertWikilink}
              />
            )}
          </div>
        )}
        {mode !== 'source' && (
          <div className="pane">
            <div className="pane-header">рендер · live</div>
            <div className="preview-pane wiki-page">
              <div className="body" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            </div>
          </div>
        )}
      </div>

      {showRevisions && revisions && (
        <div className="revisions">
          <h4>Історія ({revisions.length})</h4>
          {revisions.length === 0 && <div style={{ color: 'var(--text-dim)', fontSize: 11, padding: 8 }}>Поки порожньо</div>}
          {revisions.map((r) => (
            <div key={r.id} className="rev" onClick={() => void onRestore(r.id)} title="Клік — відкотити">
              <span>{new Date(r.savedAt * 1000).toLocaleString('uk-UA')}</span>
              <span>{r.bodyLength}b</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FmField({
  label, value, onChange, mono = false, required = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  mono?: boolean;
  required?: boolean;
}): JSX.Element {
  return (
    <div className={`fm-field ${required && !value ? 'error' : ''}`}>
      <label>{label}{required ? ' *' : ''}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={mono ? { fontFamily: '"Spline Sans Mono", monospace' } : undefined}
      />
    </div>
  );
}

function Autocomplete({
  x, y, matches, active, onPick,
}: {
  x: number; y: number;
  matches: string[]; active: number;
  onPick: (slug: string) => void;
}): JSX.Element {
  return (
    <div className="autocomplete" style={{ left: x, top: y }}>
      {matches.length === 0 && <div className="ac-empty">нічого не знайдено</div>}
      {matches.map((s, i) => (
        <button
          key={s}
          className={`ac-item ${i === active ? 'active' : ''}`}
          type="button"
          onMouseDown={(e) => { e.preventDefault(); onPick(s); }}
          style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <span className="ac-slug">{s.length > 36 ? s.slice(0, 36) + '…' : s}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Дуже груба позиція курсора у textarea. Замість каретно-точного розрахунку
 * (який вимагає mirror-діва) — ставимо popup просто під полем, поки cursor-у
 * не вдалось локалізувати. Покращення — у Ф7 polish.
 */
function approximateCaret(ta: HTMLTextAreaElement): { x: number; y: number } {
  const rect = ta.getBoundingClientRect();
  return { x: 20, y: rect.height * 0.5 };
}
